import { useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown, FolderOpen, Pencil, Plus, Star, Trash2, Video, Link2, Loader2, Images, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtError, mediaUrl, youtubeEmbed } from "../../lib/api";
import { useContent } from "../../context/SiteContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { FieldInput, Uploader } from "./FieldInput";
import { ALBUM_CATEGORIES } from "./resources";
import { defaults } from "./ResourceManager";

const ALBUM_FIELDS = [
  { name: "title", label: "Album name", required: true },
  { name: "description", label: "Description", type: "textarea", rows: 3 },
  { name: "category", label: "Category", type: "select", options: ALBUM_CATEGORIES },
  { name: "cover_image", label: "Cover image", type: "image", hint: "Or set any photo in the album as cover using the star button." },
  { name: "published", label: "Status", type: "switch", on: "Published – visible on website", off: "Unpublished – hidden" },
];

const run = async (fn, ok) => {
  try {
    await fn();
    if (ok) toast.success(ok);
    return true;
  } catch (err) {
    toast.error(fmtError(err));
    return false;
  }
};

const reorder = (resource, list, i, dir) => {
  const ids = list.map((x) => x.id);
  [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
  return api.post(`/admin/content/${resource}/reorder`, { ids });
};

const FormDialog = ({ title, fields, form, setForm, onSave }) => {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onSave();
    setBusy(false);
  };
  return (
    <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="gallery-form-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {form && (
          <form onSubmit={submit} className="space-y-4">
            {fields.map((f) => (
              <div key={f.name}>
                <span className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}{f.required && " *"}</span>
                <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} form={form} setForm={setForm} />
              </div>
            ))}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} className="btn-outline text-sm" data-testid="gallery-form-cancel">Cancel</button>
              <button disabled={busy || fields.some((f) => f.required && !form[f.name])} className="btn-primary text-sm disabled:opacity-50" data-testid="gallery-form-save">{busy ? "Saving..." : "Save"}</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ItemThumb = ({ it }) => {
  if (it.media_type !== "video") return <img src={mediaUrl(it.thumb || it.url)} alt={it.title} className="h-full w-full object-cover" />;
  const yt = youtubeEmbed(it.url);
  return (
    <div className="relative h-full w-full bg-slate-800">
      {yt ? <img src={`https://img.youtube.com/vi/${yt.split("/").pop()}/hqdefault.jpg`} alt="" className="h-full w-full object-cover" /> : <video src={mediaUrl(it.url)} className="h-full w-full object-cover" muted />}
      <PlayCircle className="absolute inset-0 m-auto h-10 w-10 text-white drop-shadow" />
    </div>
  );
};

const AlbumView = ({ album, items, reload, onBack, onEditAlbum }) => {
  const [link, setLink] = useState("");
  const [edit, setEdit] = useState(null);
  const add = (payload) => api.post("/content/gallery", { album_id: album.id, order: 1000 + items.length, ...payload });
  const addUploads = async (res, media_type) => {
    for (const r of res) await add({ url: r.url, thumb: r.thumb || "", media_type, title: "" });
    reload();
  };
  const addLink = async () => {
    if (!youtubeEmbed(link)) return toast.error("Please paste a valid YouTube link");
    if (await run(() => add({ url: link, media_type: "video", title: "" }), "Video added")) { setLink(""); reload(); }
  };
  const setCover = (it) => run(() => api.put(`/content/albums/${album.id}`, { ...album, cover_image: it.thumb || it.url }), "Cover image updated").then(reload);
  const remove = (it) => window.confirm("Delete this item from the album?") && run(() => api.delete(`/content/gallery/${it.id}`), "Deleted").then(reload);
  const move = (i, dir) => run(() => reorder("gallery", items, i, dir)).then(reload);
  return (
    <div data-testid="admin-album-view">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700 mb-4" data-testid="album-back-btn"><ArrowLeft className="h-4 w-4" /> All albums</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#0B1F44]" data-testid="album-view-title">{album.title}</h2>
          <p className="text-sm text-slate-500">{album.category} · {items.length} items · {album.published === false ? "Unpublished" : "Published"}</p>
        </div>
        <button onClick={onEditAlbum} className="btn-outline text-sm" data-testid="album-edit-btn"><Pencil className="h-4 w-4" /> Edit album</button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6 flex flex-wrap items-center gap-3">
        <Uploader accept="image/jpeg,image/png,image/webp,image/gif" multiple label="Upload photos" testid="album-photos" onDone={(r) => addUploads(r, "photo")} />
        <Uploader accept="video/mp4,video/webm" multiple label="Upload videos" testid="album-videos" onDone={(r) => addUploads(r, "video")} />
        <div className="flex flex-1 min-w-[260px] gap-2">
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste YouTube video link" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="album-video-link-input" />
          <button onClick={addLink} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium hover:bg-slate-50" data-testid="album-video-link-add"><Link2 className="h-4 w-4" /> Add link</button>
        </div>
      </div>
      {items.length === 0 ? <p className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="album-empty">No photos or videos yet. Use the buttons above to add some.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="album-items-grid">
          {items.map((it, i) => {
            const isCover = album.cover_image && [it.thumb, it.url].includes(album.cover_image);
            return (
              <div key={it.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`album-item-${it.id}`}>
                <div className="aspect-[4/3] relative">
                  <ItemThumb it={it} />
                  {isCover && <span className="absolute top-2 left-2 rounded-full bg-amber-400 text-[#0B1F44] px-2 py-0.5 text-xs font-bold">Cover</span>}
                </div>
                <div className="p-2.5">
                  <p className="text-xs text-slate-600 truncate mb-2">{it.title || <span className="text-slate-400">No caption</span>}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex">
                      <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-20" aria-label="Move earlier" data-testid={`album-item-left-${it.id}`}><ArrowLeft className="h-3.5 w-3.5" /></button>
                      <button disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-20" aria-label="Move later" data-testid={`album-item-right-${it.id}`}><ArrowRight className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="flex">
                      {it.media_type === "photo" && <button onClick={() => setCover(it)} className={`p-1.5 rounded hover:bg-amber-50 ${isCover ? "text-amber-500" : "text-slate-400"}`} title="Set as cover" data-testid={`album-item-cover-${it.id}`}><Star className="h-3.5 w-3.5" /></button>}
                      <button onClick={() => setEdit({ ...it })} className="p-1.5 rounded hover:bg-slate-100" title="Edit caption" data-testid={`album-item-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(it)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete" data-testid={`album-item-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <FormDialog title="Edit item" form={edit} setForm={setEdit} fields={[{ name: "title", label: "Caption" }]}
        onSave={async () => { if (await run(() => api.put(`/content/gallery/${edit.id}`, edit), "Saved")) { setEdit(null); reload(); } }} />
    </div>
  );
};

export const GalleryManager = () => {
  const { items: albums, reload: reloadAlbums } = useContent("albums", true);
  const { items: gallery, reload: reloadItems } = useContent("gallery", true);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState(null);
  const reload = () => { reloadAlbums(); reloadItems(); };
  const itemsOf = (id) => (gallery || []).filter((g) => g.album_id === id);
  const save = async () => {
    const ok = await run(() => (form.id ? api.put(`/content/albums/${form.id}`, form) : api.post("/content/albums", { ...form, order: 1000 + albums.length })), "Album saved — changes are live");
    if (ok) { setForm(null); reload(); }
  };
  const togglePublish = (a, v) => run(() => api.put(`/content/albums/${a.id}`, { ...a, published: v }), v ? "Album published" : "Album unpublished").then(reload);
  const remove = (a) => window.confirm(`Delete album "${a.title}" and all its ${itemsOf(a.id).length} items?`) && run(() => api.delete(`/content/albums/${a.id}`), "Album deleted").then(reload);
  const open = albums?.find((a) => a.id === openId);

  const dialog = <FormDialog title={form?.id ? "Edit album" : "New album"} fields={ALBUM_FIELDS} form={form} setForm={setForm} onSave={save} />;
  if (open) return <><AlbumView album={open} items={itemsOf(open.id)} reload={reload} onBack={() => setOpenId(null)} onEditAlbum={() => setForm({ ...open })} />{dialog}</>;
  return (
    <div data-testid="admin-gallery-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold text-[#0B1F44]">Gallery Management</h2>
        <button onClick={() => setForm(defaults(ALBUM_FIELDS))} className="btn-primary text-sm" data-testid="admin-add-album"><Plus className="h-4 w-4" /> New album</button>
      </div>
      {!albums || !gallery ? <p className="p-8 text-center text-slate-500 flex justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</p>
        : albums.length === 0 ? <p className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="albums-empty">No albums yet. Create an album, then upload photos and videos into it.</p> : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {albums.map((a, i) => {
              const its = itemsOf(a.id);
              const cover = a.cover_image || its.find((x) => x.media_type === "photo")?.thumb || its.find((x) => x.media_type === "photo")?.url;
              return (
                <div key={a.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`admin-album-${a.id}`}>
                  <button onClick={() => setOpenId(a.id)} className="block w-full aspect-[16/10] bg-slate-100 relative" data-testid={`admin-album-open-${a.id}`}>
                    {cover ? <img src={mediaUrl(cover)} alt="" className="h-full w-full object-cover" /> : <Images className="h-10 w-10 text-slate-300 absolute inset-0 m-auto" />}
                    {a.published === false && <span className="absolute top-2 left-2 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 px-2 py-0.5 text-xs font-semibold">Unpublished</span>}
                  </button>
                  <div className="p-4">
                    <p className="font-semibold text-[#0B1F44] truncate">{a.title}</p>
                    <p className="text-xs text-slate-500">{a.category} · {its.filter((x) => x.media_type === "photo").length} photos · {its.filter((x) => x.media_type === "video").length} videos</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={a.published !== false} onCheckedChange={(v) => togglePublish(a, v)} data-testid={`admin-album-publish-${a.id}`} />Published</span>
                      <div className="flex">
                        <button disabled={i === 0} onClick={() => run(() => reorder("albums", albums, i, -1)).then(reload)} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-20" aria-label="Move up" data-testid={`admin-album-up-${a.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                        <button disabled={i === albums.length - 1} onClick={() => run(() => reorder("albums", albums, i, 1)).then(reload)} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-20" aria-label="Move down" data-testid={`admin-album-down-${a.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setOpenId(a.id)} className="p-1.5 rounded hover:bg-slate-100" title="Open" data-testid={`admin-album-manage-${a.id}`}><FolderOpen className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setForm({ ...a })} className="p-1.5 rounded hover:bg-slate-100" title="Edit" data-testid={`admin-album-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => remove(a)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete" data-testid={`admin-album-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      {dialog}
      <p className="mt-6 text-xs text-slate-500 flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> Photos are automatically resized and optimised on upload. Videos can be uploaded (MP4/WEBM) or added as YouTube links.</p>
    </div>
  );
};
